import { useState } from "react";

import { IAdminUserDetail } from "../../../../@types/admin/AdminPatient";
import { useChangePatientPassword } from "../../../../hooks/mutations/admin/patients";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  FieldError,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../AdminPage.styles";

interface IChangePasswordModalProps {
  user: IAdminUserDetail;
  onDone: () => void;
}

/**
 * Sets a new password for the patient's account (EN-5169) — Retool's
 * `modalChangePassword`.
 *
 * ⚠️ This takes over a patient's account credentials. Retool guarded it with
 * its generic `confirmationMessage` ("Are you sure you want to change the
 * password?") on top of the form; here the warning is in the dialog that is
 * already asking, so there is one prompt rather than two.
 *
 * The endpoint takes the USER id even though its route reads
 * `/Retool/Patients/{userId}/ChangePassword`.
 */
export function ChangePasswordModal({
  user,
  onDone,
}: IChangePasswordModalProps) {
  const { showToast } = useToast();
  const { changePassword, isChangingPassword } = useChangePatientPassword();

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [touched, setTouched] = useState(false);

  // Retool disabled Save on `value1 !== value2 || value1 === ""` and said
  // nothing about why. The same rule, with the reason shown once the field has
  // been touched (a create-shaped form should not shout before it is used).
  const error = (() => {
    if (password.length === 0) return "Enter a new password.";
    if (password !== confirmation) return "The two passwords don't match.";
    return undefined;
  })();

  const handleSave = async () => {
    if (error) return;

    try {
      await changePassword({ userId: user.id, newPassword: password });
      showToast("Password was updated", "success");
      onDone();
    } catch (caught) {
      // The endpoint rejects a password that fails the policy and says which
      // rule; Retool showed that message under the fields.
      showToast(
        errorToastMessage(caught, "There was an error changing the password."),
        "warning"
      );
    }
  };

  const userName = `${user.firstName} ${user.lastName}`.trim();

  return (
    <ModalContainer>
      <ModalTitle>Change password</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Set a new password for{" "}
          <strong>{userName || `user ${user.id}`}</strong>
          {user.email ? ` (${user.email})` : ""}.
        </ModalBodyText>

        <WarningNotice>
          The patient is not told. They will not be able to sign in until
          somebody gives them the new password.
        </WarningNotice>

        <FilterGroup>
          <FilterLabel htmlFor="new-password">New password</FilterLabel>
          <FilterInput
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setTouched(true);
            }}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="new-password-again">
            New password (again)
          </FilterLabel>
          <FilterInput
            id="new-password-again"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => {
              setConfirmation(e.target.value);
              setTouched(true);
            }}
          />
          {touched && error && <FieldError>{error}</FieldError>}
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isChangingPassword}
            onClick={onDone}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isChangingPassword || Boolean(error)}
            onClick={handleSave}
          >
            {isChangingPassword ? "Saving..." : "Change password"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
